'use client';

import { motion } from 'motion/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@workspace/ui/components/accordion';

import { faqs } from '~/constants/homepage/faqs';

type FAQItem = {
  question: string;
  answer: React.ReactNode;
};

export default function FAQSection({ faq: externalFaq }: { faq?: FAQItem[] } = {}) {
  const faqData = externalFaq
    ? externalFaq.map((item) => ({ q: item.question, a: item.answer }))
    : faqs;
  return (
    <section id="faq" className="py-14 sm:py-20 md:py-24 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center rounded-full border border-primary/20 px-3.5 py-1 text-xs font-medium text-primary mb-6 bg-videomule-light-green dark:bg-primary/10">
              FAQs
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground text-balance"
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-3 text-muted-foreground text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-relaxed"
          >
            Got questions? We&apos;ve got answers.
          </motion.p>
        </div>

        {/* Accordion */}
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
            {faqData.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
              >
                <AccordionItem
                  value={`item-${i}`}
                  className="bg-card border border-border rounded-xl px-5 sm:px-6 hover:border-primary/30 hover:shadow-md transition-all duration-300 data-[state=open]:border-primary/40 data-[state=open]:shadow-lg"
                >
                  <AccordionTrigger className="text-sm sm:text-base md:text-lg font-semibold text-foreground hover:no-underline hover:text-primary py-4 sm:py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4 sm:pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Section separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-border to-transparent" />
    </section>
  );
}
